/**
 * Beloved — Prayer intentions (the continuity "moat")
 *
 * This is what lets Beloved gently remember what a person is carrying:
 * "Continue praying for someone you love." An intention is simply the theme
 * they chose and the words they wrote, kept ONLY on this device.
 *
 * Privacy is the whole point. A person's own words about their health, their
 * family, or their grief are deeply personal religious data. They live in this
 * browser's localStorage and nowhere else. Nothing here ever sends them across
 * a network; the app only passes them transiently into the prayer service when
 * a fresh prayer is being written, exactly as if the person had typed them again.
 *
 * We keep at most five intentions, newest first, and we never duplicate a
 * theme: praying again about the same theme simply refreshes the existing one.
 *
 * Every function is guarded and never throws — a blocked or full storage just
 * means the app quietly behaves as if there were no intentions.
 */

/** localStorage key holding the array of intentions (newest first). */
export const STORAGE_KEY = 'beloved_intentions';

/** The most intentions we ever keep. Older ones fall off the end. */
export const MAX_INTENTIONS = 5;

/**
 * A single stored intention.
 * @typedef {Object} Intention
 * @property {string} id a stable id for this intention
 * @property {string} theme the prayer theme id (e.g. 'family', 'health')
 * @property {string} words the person's own step-2 words (may be empty)
 * @property {string} label the friendly label to show (e.g. 'Someone I love')
 * @property {number} createdAt when it was first remembered (ms since epoch)
 * @property {number} lastPrayedAt when it was most recently prayed (ms since epoch)
 */

/**
 * Read the raw stored array, quietly repairing anything unexpected.
 * @returns {Intention[]} the stored intentions, or an empty array
 */
function readRaw() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // A blocked or corrupt storage simply reads as "no intentions".
    return [];
  }
}

/**
 * Persist the intentions array. Never throws (a blocked storage is harmless).
 * @param {Intention[]} intentions the array to store
 * @returns {void}
 */
function writeRaw(intentions) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(intentions));
  } catch (e) {
    // If storage is full or blocked, we simply don't persist; not worth surfacing.
  }
}

/**
 * Keep only well-formed intentions, so a hand-edited or partial store can never
 * break the UI. Each kept item is normalised to the full shape.
 * @param {unknown[]} list a possibly-messy array
 * @returns {Intention[]} only the valid, normalised intentions
 */
function sanitize(list) {
  const now = Date.now();
  return list
    .filter((it) => it && typeof it === 'object' && typeof it.theme === 'string' && it.theme)
    .map((it) => ({
      id: typeof it.id === 'string' && it.id ? it.id : makeId(),
      theme: it.theme,
      words: typeof it.words === 'string' ? it.words : '',
      label: typeof it.label === 'string' && it.label ? it.label : it.theme,
      createdAt: Number.isFinite(it.createdAt) ? it.createdAt : now,
      lastPrayedAt: Number.isFinite(it.lastPrayedAt) ? it.lastPrayedAt : now,
    }));
}

/**
 * Make a reasonably-unique id for a new intention.
 * @returns {string}
 */
function makeId() {
  return `intention-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

/**
 * All remembered intentions, newest first, capped at {@link MAX_INTENTIONS}.
 * @returns {Intention[]}
 */
export function getIntentions() {
  return sanitize(readRaw()).slice(0, MAX_INTENTIONS);
}

/**
 * Remember an intention, or refresh it if the same theme is already held.
 *
 * Praying about a theme the person already carries does NOT create a duplicate:
 * the existing intention keeps its original `createdAt` but takes the newest
 * `words` and `label`, has its `lastPrayedAt` set to now, and moves to the
 * front (newest first). A brand-new theme is added to the front and the list is
 * capped so only the five most recent survive.
 *
 * @param {{theme: string, words?: string, label?: string}} intention the theme,
 *   the person's own words (optional), and a friendly label (optional; falls
 *   back to the theme id)
 * @returns {Intention[]} the updated list (newest first)
 */
export function rememberIntention({ theme, words = '', label } = {}) {
  if (!theme || typeof theme !== 'string') return getIntentions();

  const now = Date.now();
  const existing = getIntentions();
  const prior = existing.find((it) => it.theme === theme);
  const rest = existing.filter((it) => it.theme !== theme);

  const refreshed = {
    id: prior ? prior.id : makeId(),
    theme,
    words: typeof words === 'string' ? words : '',
    label: label || (prior && prior.label) || theme,
    createdAt: prior ? prior.createdAt : now,
    lastPrayedAt: now,
  };

  const next = [refreshed, ...rest].slice(0, MAX_INTENTIONS);
  writeRaw(next);
  return next;
}

/**
 * Note that an intention was prayed just now (updates its `lastPrayedAt` and
 * moves it to the front). Used when a prayer is generated from a stored
 * intention via the Today card. A missing id is a harmless no-op.
 *
 * @param {string} id the intention's id
 * @returns {Intention[]} the updated list (newest first)
 */
export function markPrayed(id) {
  const now = Date.now();
  const existing = getIntentions();
  const target = existing.find((it) => it.id === id);
  if (!target) return existing;

  const rest = existing.filter((it) => it.id !== id);
  const next = [{ ...target, lastPrayedAt: now }, ...rest].slice(0, MAX_INTENTIONS);
  writeRaw(next);
  return next;
}

/**
 * Gently let go of an intention ("That's answered now"). Removing an unknown id
 * simply leaves the list unchanged.
 *
 * @param {string} id the intention's id
 * @returns {Intention[]} the updated list (newest first)
 */
export function resolveIntention(id) {
  const next = getIntentions().filter((it) => it.id !== id);
  writeRaw(next);
  return next;
}

export default getIntentions;
